'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';

interface LabelCardProps {
  labels: { id: string; name: string }[];
}

const LabelCard = ({ labels }: LabelCardProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const cardRef = useRef<HTMLDivElement>(null);

  const updateURL = useCallback(
    (labelId: string | null) => {
      const newSearchParams = new URLSearchParams(searchParams);
      //remove the page and clear the url
      newSearchParams.delete('page');

      if (labelId) {
        newSearchParams.set('label', labelId);
      } else {
        newSearchParams.delete('label');
      }
      router.replace(`/?${newSearchParams.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleLabelClick = useCallback(
    (labelId: string) => {
      updateURL(labelId);
    },
    [updateURL]
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        if (
          !event.target ||
          !(event.target as Element).closest('.product-container')
        ) {
          updateURL(null);
        }
      }
    },
    [updateURL]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const selectedLabelId = searchParams.get('label');

  return (
    <div
      ref={cardRef}
      className="flex gap-2 flex-wrap justify-center my-4 max-w-[500px]"
    >
      {labels.map((label) => (
        <Button
          variant="outline"
          key={label.id}
          className={`cursor-pointer p-4 transition-all duration-300 rounded-lg hover:bg-green-500 hover:text-white ${
            selectedLabelId === label.name
              ? 'bg-green-500 text-white'
              : 'text-black'
          }`}
          onClick={() => handleLabelClick(label.name)}
        >
          {label.name}
        </Button>
      ))}
    </div>
  );
};

export default LabelCard;
